import type {AutoDeliveryDcxUpdateReasonStatus} from '@/consts/icc/iccAutoDeliveryStatusTypes';
import {AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES} from '@/consts/icc/iccAutoDeliveryStatusTypes';
import appContent from '@/services/content/appContent.typed';
import type {InlineNotificationModel} from '@/types/aem';
import type {AutoDeliveryInfoBoxData, AutoDeliveryInfoBoxSource} from '@/types/content';
import {AUTO_DELIVERY_INFO_BOX_SOURCES} from '@/types/content';

const ON_HOLD_CONTENT_VARIANTS = {
    PAYMENT_FAILURE: 'paymentFailure',
    PRICE_CHANGED: 'priceChanged',
} as const;

type OnHoldContentVariant = (typeof ON_HOLD_CONTENT_VARIANTS)[keyof typeof ON_HOLD_CONTENT_VARIANTS];

const getOnHoldContentVariant = (
    statusUpdateReason: AutoDeliveryDcxUpdateReasonStatus | null
): OnHoldContentVariant | null => {
    if (!statusUpdateReason) return null;

    switch (statusUpdateReason) {
        // TODO: replace with correct flag once known — using this one as a placeholder
        case AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES.AD_USER_PRICE_CHANGE_CONSENT_PERIOD_EXPIRED:
            return ON_HOLD_CONTENT_VARIANTS.PRICE_CHANGED;
        default:
            return ON_HOLD_CONTENT_VARIANTS.PAYMENT_FAILURE;
    }
};

export const getOnHoldInfoBoxData = (
    statusUpdateReason: AutoDeliveryDcxUpdateReasonStatus | null,
    source: AutoDeliveryInfoBoxSource
): AutoDeliveryInfoBoxData | undefined => {
    const variant = getOnHoldContentVariant(statusUpdateReason);

    if (!variant) return undefined;

    const content = appContent.getAutoDeliveryContent();
    const isDashboard = source === AUTO_DELIVERY_INFO_BOX_SOURCES.DASHBOARD;

    const buildResult = (notification: InlineNotificationModel | undefined): AutoDeliveryInfoBoxData | undefined => {
        if (!notification) return undefined;

        return {notification};
    };

    switch (variant) {
        case ON_HOLD_CONTENT_VARIANTS.PAYMENT_FAILURE:
            return buildResult(
                isDashboard
                    ? content?.dashboardContent?.onHoldOrderPaymentFailureIssueDashboard?.inlineNotification
                    : content?.extendedDetailsContent?.onHoldOrderPaymentFailureIssue?.orderDetailsInlineNotification
            );
        case ON_HOLD_CONTENT_VARIANTS.PRICE_CHANGED:
            return buildResult(
                isDashboard
                    ? content?.dashboardContent?.onHoldOrderPaymentPriceChangedDashboard?.inlineNotification
                    : content?.extendedDetailsContent?.onHoldOrderPaymentPriceChanged?.orderDetailsInlineNotification
            );
        default:
            return undefined;
    }
};
